import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { NumberField, StateField } from '../../components/form';
import ReadOnlyField from '../../components/form/readonly-field';
import {
  AddressValidationResult,
  Dealer,
  LoadUnitLabels,
  SNOW_LOAD_OVERRIDE_OPTIONS,
  WIND_LOAD_OVERRIDE_OPTIONS,
} from '../../types';
import type { JobLocationMode, ValidationStatus } from '../../types/address-validation-types';
import { getErrorMessage } from '../../utils';
import {
  useGetAsceLoadRatingsMutation,
  useValidateAddressMutation,
  useValidateCoordinatesMutation,
} from './orders-api-slice';

interface StepThreeProps {
  onFormChange: () => void;
  dealers: Dealer[];
}

const INDEMNIFICATION_TEXT =
  'By checking the box below, I certify that I have manually entered the Wind and Snow load ratings and accept full responsibility for the accuracy of these values. I understand that bypassing the automated ASCE address-based load rating lookup may result in incorrect load values. I hereby indemnify and hold harmless the company, its officers, employees, and agents from any and all claims, damages, losses, or liabilities arising from incorrect or inappropriate load ratings entered through this manual override.';

const StepThree: React.FC<StepThreeProps> = ({ onFormChange, dealers }) => {
  const { control, setValue, getValues } = useFormContext();

  // Local UI state
  const [locationMode, setLocationMode] = useState<JobLocationMode>('address');
  const [validationStatus, setValidationStatus] = useState<ValidationStatus>('none');
  const [isValidating, setIsValidating] = useState(false);
  const [isLoadingAsce, setIsLoadingAsce] = useState(false);
  const [overrideMode, setOverrideMode] = useState(false);
  const [certificationChecked, setCertificationChecked] = useState(false);
  const [nhWarning, setNhWarning] = useState(false);

  // Address suggestion dialog
  const [suggestionDialogOpen, setSuggestionDialogOpen] = useState(false);
  const [suggestedAddress, setSuggestedAddress] = useState<AddressValidationResult | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Local address fields (not in the main form to avoid coupling)
  const [jobAddress1, setJobAddress1] = useState('');
  const [jobAddress2, setJobAddress2] = useState('');
  const [jobCity, setJobCity] = useState('');
  const [jobState, setJobState] = useState('');
  const [jobZip, setJobZip] = useState('');
  const [jobLat, setJobLat] = useState('');
  const [jobLon, setJobLon] = useState('');

  // Watch form fields
  const confirmedLoadRating = useWatch({ control, name: 'confirmedLoadRating' });
  const windLoad = useWatch({ control, name: 'windLoad' });
  const snowLoad = useWatch({ control, name: 'snowLoad' });
  const shippingAddress = useWatch({ control, name: 'shippingAddress' });
  const deliveryType = useWatch({ control, name: 'deliveryType' });
  const dealerId = useWatch({ control, name: 'dealerId' });

  // RTK Query mutations
  const [validateAddress] = useValidateAddressMutation();
  const [validateCoordinates] = useValidateCoordinatesMutation();
  const [getAsceLoadRatings] = useGetAsceLoadRatingsMutation();

  // Pre-populate from shipping address for delivery orders
  useEffect(() => {
    if (
      deliveryType === 'Delivery' &&
      shippingAddress &&
      validationStatus === 'none' &&
      !jobAddress1
    ) {
      setJobAddress1(shippingAddress.address1 || '');
      setJobAddress2(shippingAddress.address2 || '');
      setJobCity(shippingAddress.city || '');
      setJobState(shippingAddress.state || '');
      setJobZip(shippingAddress.zip || '');
    }
  }, [deliveryType, shippingAddress, validationStatus, jobAddress1]);

  // Trigger form validation on relevant changes
  useEffect(() => {
    onFormChange();
  }, [windLoad, snowLoad, confirmedLoadRating, validationStatus, overrideMode, onFormChange]);

  // In Address Validation Override mode we skip Google/ASCE validation but still capture the
  // jobsite on the order (required for Pickup, since the shipping address is the plant — not
  // the jobsite). Mirror the entered fields into the form's jobLocation with an 'override'
  // status as the user types.
  useEffect(() => {
    if (!overrideMode) return;
    const hasAddress = Boolean(jobAddress1 && jobCity && jobState && jobZip);
    const latNum = parseFloat(jobLat);
    const lonNum = parseFloat(jobLon);
    const hasCoords = !isNaN(latNum) && !isNaN(lonNum);

    if (!hasAddress && !hasCoords) {
      setValue('jobLocation', undefined);
      return;
    }

    setValue('jobLocation', {
      address1: jobAddress1,
      address2: jobAddress2,
      city: jobCity,
      state: jobState,
      zip: jobZip,
      latitude: hasCoords ? latNum : undefined,
      longitude: hasCoords ? lonNum : undefined,
      validationStatus: 'override',
      validationMode: locationMode,
    });
    if (jobState) {
      setValue('buildingState', jobState, { shouldValidate: true });
    }
    onFormChange();
  }, [
    overrideMode,
    jobAddress1,
    jobAddress2,
    jobCity,
    jobState,
    jobZip,
    jobLat,
    jobLon,
    locationMode,
    setValue,
    onFormChange,
  ]);

  const resetValidationState = useCallback(() => {
    setValidationStatus('none');
    setValue('windLoad', 0, { shouldValidate: true });
    setValue('snowLoad', 0, { shouldValidate: true });
    setValue('confirmedLoadRating', false, { shouldValidate: true });
    setNhWarning(false);
  }, [setValue]);

  const fetchAsceLoadRatings = useCallback(
    async (lat: number, lon: number) => {
      setIsLoadingAsce(true);
      try {
        const result = await getAsceLoadRatings({ lat, lon, riskLevel: 1 }).unwrap();
        setValue('windLoad', result.windLoadMph, { shouldValidate: true });
        setValue('snowLoad', result.snowLoadPsf, { shouldValidate: true });
        setValue('confirmedLoadRating', false, { shouldValidate: true });

        if (result.isNewHampshire_7_16) {
          setNhWarning(true);
        }

        // Store ASCE data on the form for persistence
        setValue('asceData', {
          windLoadMph: result.windLoadMph,
          snowLoadPsf: result.snowLoadPsf,
          elevation: result.elevation,
          riskLevel: result.riskLevel,
          isHurricaneWindDebrisZone: result.isHurricaneWindDebrisZone,
          isSpecialWindZone: result.isSpecialWindZone,
          isNewHampshire_7_16: result.isNewHampshire_7_16,
          mriWindResults: result.mriWindResults,
        });
      } catch (err: unknown) {
        setValue('windLoad', 0, { shouldValidate: true });
        setValue('snowLoad', 0, { shouldValidate: true });
        const extracted = getErrorMessage(err);
        const msg = extracted.includes('not available for this location')
          ? extracted
          : 'Unable to retrieve load ratings from ASCE. Please try again later.';
        setErrorMessage(msg);
        setErrorDialogOpen(true);
      } finally {
        setIsLoadingAsce(false);
      }
    },
    [getAsceLoadRatings, setValue]
  );

  const handleValidateAddress = async () => {
    if (!jobAddress1 || !jobCity || !jobState || !jobZip) return;

    setIsValidating(true);
    setValidationStatus('validating');

    try {
      const result = await validateAddress({
        address1: jobAddress1,
        address2: jobAddress2,
        city: jobCity,
        state: jobState,
        zip: jobZip,
      }).unwrap();

      if (result.isValid && !result.hasSuggestion) {
        setValidationStatus('validated');
        setJobLat(result.latitude.toString());
        setJobLon(result.longitude.toString());
        updateFormJobLocation(result.latitude, result.longitude);
        await fetchAsceLoadRatings(result.latitude, result.longitude);
      } else if (result.hasSuggestion && result.suggestedAddress) {
        setSuggestedAddress(result);
        setSuggestionDialogOpen(true);
      } else {
        setValidationStatus('failed');
        setErrorMessage(
          result.errorMessage ||
            "We couldn't validate this address with Google Maps. Please check the address/coordinates and try again."
        );
        setErrorDialogOpen(true);
      }
    } catch {
      setValidationStatus('failed');
      setErrorMessage(
        'Unable to validate address at this time. Please try again later or enter coordinates manually.'
      );
      setErrorDialogOpen(true);
    } finally {
      setIsValidating(false);
    }
  };

  const handleValidateCoordinates = async () => {
    const lat = parseFloat(jobLat);
    const lon = parseFloat(jobLon);

    if (isNaN(lat) || isNaN(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      return;
    }

    setIsValidating(true);
    setValidationStatus('validating');

    try {
      const result = await validateCoordinates({ latitude: lat, longitude: lon }).unwrap();
      if (result.isValid) {
        setValidationStatus('validated');
        if (result.validatedAddress) {
          setJobAddress1(result.validatedAddress.address1 || '');
          setJobCity(result.validatedAddress.city || '');
          setJobState(result.validatedAddress.state || '');
          setJobZip(result.validatedAddress.zip || '');
        }
        updateFormJobLocation(lat, lon);
        await fetchAsceLoadRatings(lat, lon);
      } else {
        setValidationStatus('failed');
        setErrorMessage("We couldn't validate these coordinates. Please check and try again.");
        setErrorDialogOpen(true);
      }
    } catch {
      setValidationStatus('failed');
      setErrorMessage('Unable to validate coordinates at this time. Please try again later.');
      setErrorDialogOpen(true);
    } finally {
      setIsValidating(false);
    }
  };

  const handleUseSuggestedAddress = async () => {
    if (!suggestedAddress?.suggestedAddress) return;
    const suggested = suggestedAddress.suggestedAddress;
    const resolvedAddress = {
      address1: suggested.address1,
      address2: suggested.address2 || '',
      city: suggested.city,
      state: suggested.state,
      zip: suggested.zip,
    };

    setJobAddress1(resolvedAddress.address1);
    setJobAddress2(resolvedAddress.address2);
    setJobCity(resolvedAddress.city);
    setJobState(resolvedAddress.state);
    setJobZip(resolvedAddress.zip);
    setJobLat(suggestedAddress.latitude.toString());
    setJobLon(suggestedAddress.longitude.toString());

    setValidationStatus('validated');
    setSuggestionDialogOpen(false);
    updateFormJobLocation(suggestedAddress.latitude, suggestedAddress.longitude, resolvedAddress);

    // UDOMI-980: preserve dealer location identity fields required by orderFormSchema
    if (deliveryType === 'Delivery') {
      const existingShipping = getValues('shippingAddress');
      const dealerId = getValues('dealerId');
      setValue('shippingAddress', {
        dealerId: existingShipping?.dealerId ?? dealerId,
        name: existingShipping?.name ?? 'Delivery Address',
        address1: resolvedAddress.address1,
        address2: resolvedAddress.address2 || null,
        city: resolvedAddress.city,
        state: resolvedAddress.state,
        zip: resolvedAddress.zip,
        phone: existingShipping?.phone ?? null,
        email: existingShipping?.email ?? null,
      });
    }

    await fetchAsceLoadRatings(suggestedAddress.latitude, suggestedAddress.longitude);
  };

  const handleKeepOriginalAddress = async () => {
    if (!suggestedAddress) return;

    setJobLat(suggestedAddress.latitude.toString());
    setJobLon(suggestedAddress.longitude.toString());

    setValidationStatus('validated');
    setSuggestionDialogOpen(false);
    updateFormJobLocation(suggestedAddress.latitude, suggestedAddress.longitude);
    await fetchAsceLoadRatings(suggestedAddress.latitude, suggestedAddress.longitude);
  };

  const updateFormJobLocation = (
    lat: number,
    lon: number,
    address?: {
      address1: string;
      address2: string;
      city: string;
      state: string;
      zip: string;
    }
  ) => {
    const resolvedAddress = address ?? {
      address1: jobAddress1,
      address2: jobAddress2,
      city: jobCity,
      state: jobState,
      zip: jobZip,
    };

    setValue('jobLocation', {
      address1: resolvedAddress.address1,
      address2: resolvedAddress.address2,
      city: resolvedAddress.city,
      state: resolvedAddress.state,
      zip: resolvedAddress.zip,
      latitude: lat,
      longitude: lon,
      validationStatus: 'validated',
      validationMode: locationMode,
    });

    if (resolvedAddress.state) {
      setValue('buildingState', resolvedAddress.state, { shouldValidate: true });
    }
  };

  const handleLocationModeChange = (mode: JobLocationMode) => {
    setLocationMode(mode);
    if (validationStatus === 'validated') {
      resetValidationState();
    }
  };

  const handleOverrideToggle = (checked: boolean) => {
    setOverrideMode(checked);
    setValue('addressValidationOverride', checked);
    if (checked) {
      setCertificationChecked(false);
      setValue('manualLoadRatingCertified', false);
      // Auto-populate buildingState from the relevant address for the selected delivery type
      const stateFromAddress =
        deliveryType === 'Pickup'
          ? dealers.find(d => d.id === dealerId)?.state
          : shippingAddress?.state;
      if (stateFromAddress) {
        setValue('buildingState', stateFromAddress, { shouldValidate: true });
      }
    } else {
      setValue('windLoad', 0, { shouldValidate: true });
      setValue('snowLoad', 0, { shouldValidate: true });
      setCertificationChecked(false);
      setValue('manualLoadRatingCertified', false);
      setValue('confirmedLoadRating', false, { shouldValidate: true });
      setValue('buildingState', '', { shouldValidate: true });
      // Clear the override-captured jobsite so the user must re-validate via the normal flow.
      setValue('jobLocation', undefined, { shouldValidate: true });
      setValidationStatus('none');
    }
    onFormChange();
  };

  const isCoordinateValid =
    jobLat !== '' &&
    jobLon !== '' &&
    !isNaN(parseFloat(jobLat)) &&
    !isNaN(parseFloat(jobLon)) &&
    parseFloat(jobLat) >= -90 &&
    parseFloat(jobLat) <= 90 &&
    parseFloat(jobLon) >= -180 &&
    parseFloat(jobLon) <= 180;

  const isAddressComplete = jobAddress1 && jobCity && jobState && jobZip;

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" mb={1}>
            <Box sx={{ width: 4, height: 24, bgcolor: 'primary.main', borderRadius: 1, mr: 1 }} />
            <Typography variant="h6">Add new Order - Step 3 of 3</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter and validate the Jobsite Address (or coordinates) so the system can retrieve the
            correct ASCE Wind and Snow load ratings for all buildings in this order.
          </Typography>
        </Grid>

        {/* Address Validation Override */}
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: overrideMode ? '#fff8e1' : '#fffde7',
              border: '1px solid',
              borderColor: overrideMode ? 'warning.main' : 'warning.light',
              borderRadius: 1,
              p: 2,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={overrideMode}
                  onChange={e => handleOverrideToggle(e.target.checked)}
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Address Validation Override
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Skip address validation and manually set Wind &amp; Snow loads
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Grid>

        {/* Jobsite Address Section - hidden when override is active */}
        {!overrideMode && (
          <>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" mb={1}>
                <Box
                  sx={{ width: 4, height: 24, bgcolor: 'primary.main', borderRadius: 1, mr: 1 }}
                />
                <Typography variant="subtitle1" fontWeight={600}>
                  Jobsite Address
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (This will apply appropriate ASCE Load ratings)
                </Typography>
              </Box>
            </Grid>

            {/* Radio toggle */}
            <Grid item xs={12}>
              <RadioGroup
                row
                value={locationMode}
                onChange={e => handleLocationModeChange(e.target.value as JobLocationMode)}
              >
                <FormControlLabel
                  value="address"
                  control={<Radio color="primary" />}
                  label={
                    <Typography variant="body2" fontWeight={600} color="primary">
                      Address
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="coordinates"
                  control={<Radio color="primary" />}
                  label={
                    <Typography variant="body2" fontWeight={600} color="primary">
                      Coordinates
                    </Typography>
                  }
                />
              </RadioGroup>
            </Grid>

            {/* Address entry */}
            {locationMode === 'address' && (
              <>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Address 1"
                    required
                    value={jobAddress1}
                    onChange={e => {
                      setJobAddress1(e.target.value);
                      if (validationStatus === 'validated') resetValidationState();
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Address 2"
                    value={jobAddress2}
                    onChange={e => {
                      setJobAddress2(e.target.value);
                      if (validationStatus === 'validated') resetValidationState();
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="City"
                    required
                    value={jobCity}
                    onChange={e => {
                      setJobCity(e.target.value);
                      if (validationStatus === 'validated') resetValidationState();
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="State"
                    required
                    value={jobState}
                    onChange={e => {
                      setJobState(e.target.value.toUpperCase().slice(0, 2));
                      if (validationStatus === 'validated') resetValidationState();
                    }}
                    size="small"
                    inputProps={{ maxLength: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Zip"
                    required
                    value={jobZip}
                    onChange={e => {
                      setJobZip(e.target.value);
                      if (validationStatus === 'validated') resetValidationState();
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={handleValidateAddress}
                      disabled={!isAddressComplete || isValidating || isLoadingAsce}
                      startIcon={isValidating ? <CircularProgress size={16} /> : undefined}
                    >
                      {validationStatus === 'validated' ? 'Revalidate Address' : 'Validate Address'}
                    </Button>
                    {validationStatus === 'validated' && (
                      <Box display="flex" alignItems="center" gap={0.5} color="success.main">
                        <CheckCircleIcon fontSize="small" />
                        <Typography variant="body2" fontWeight={500}>
                          Address validated
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                {/* Show lat/long after address validation */}
                {validationStatus === 'validated' && jobLat && jobLon && (
                  <>
                    <Grid item xs={6} md={3}>
                      <TextField
                        fullWidth
                        label="Latitude"
                        value={jobLat}
                        InputProps={{ readOnly: true }}
                        size="small"
                        sx={{ '& .MuiInputBase-input': { bgcolor: '#f0f7ff' } }}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <TextField
                        fullWidth
                        label="Longitude"
                        value={jobLon}
                        InputProps={{ readOnly: true }}
                        size="small"
                        sx={{ '& .MuiInputBase-input': { bgcolor: '#f0f7ff' } }}
                      />
                    </Grid>
                  </>
                )}
              </>
            )}

            {/* Coordinates entry */}
            {locationMode === 'coordinates' && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Latitude"
                    required
                    value={jobLat}
                    onChange={e => {
                      setJobLat(e.target.value);
                      if (validationStatus === 'validated') resetValidationState();
                    }}
                    size="small"
                    type="number"
                    inputProps={{ step: 'any', min: -90, max: 90 }}
                    helperText="Range: -90 to 90"
                    error={jobLat !== '' && (parseFloat(jobLat) < -90 || parseFloat(jobLat) > 90)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Longitude"
                    required
                    value={jobLon}
                    onChange={e => {
                      setJobLon(e.target.value);
                      if (validationStatus === 'validated') resetValidationState();
                    }}
                    size="small"
                    type="number"
                    inputProps={{ step: 'any', min: -180, max: 180 }}
                    helperText="Range: -180 to 180"
                    error={jobLon !== '' && (parseFloat(jobLon) < -180 || parseFloat(jobLon) > 180)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={handleValidateCoordinates}
                      disabled={!isCoordinateValid || isValidating || isLoadingAsce}
                      startIcon={isValidating ? <CircularProgress size={16} /> : undefined}
                    >
                      {validationStatus === 'validated'
                        ? 'Revalidate Coordinates'
                        : 'Validate Coordinates'}
                    </Button>
                    {validationStatus === 'validated' && (
                      <Box display="flex" alignItems="center" gap={0.5} color="success.main">
                        <CheckCircleIcon fontSize="small" />
                        <Typography variant="body2" fontWeight={500}>
                          Coordinates validated
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                {/* Show reverse geocoded address after coordinate validation */}
                {validationStatus === 'validated' && jobAddress1 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Address (from coordinates)"
                        value={`${jobAddress1}, ${jobCity}, ${jobState} ${jobZip}`}
                        InputProps={{ readOnly: true }}
                        size="small"
                        sx={{ '& .MuiInputBase-input': { bgcolor: '#f0f7ff' } }}
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* Jobsite capture in override mode — validation skipped, but still recorded */}
        {overrideMode && (
          <>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" mb={1}>
                <Box
                  sx={{ width: 4, height: 24, bgcolor: 'primary.main', borderRadius: 1, mr: 1 }}
                />
                <Typography variant="subtitle1" fontWeight={600}>
                  Jobsite Location
                  {deliveryType === 'Pickup' && (
                    <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                      *
                    </Typography>
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (Address validation skipped — enter the jobsite for the order record)
                </Typography>
              </Box>
              {deliveryType === 'Pickup' && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Pickup orders ship to your plant, not the jobsite. Enter the jobsite address
                  and/or coordinates so it is recorded on the order.
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <RadioGroup
                row
                value={locationMode}
                onChange={e => setLocationMode(e.target.value as JobLocationMode)}
              >
                <FormControlLabel
                  value="address"
                  control={<Radio color="primary" />}
                  label={
                    <Typography variant="body2" fontWeight={600} color="primary">
                      Address
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="coordinates"
                  control={<Radio color="primary" />}
                  label={
                    <Typography variant="body2" fontWeight={600} color="primary">
                      Coordinates
                    </Typography>
                  }
                />
              </RadioGroup>
            </Grid>

            {locationMode === 'address' && (
              <>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Address 1"
                    required={deliveryType === 'Pickup'}
                    value={jobAddress1}
                    onChange={e => setJobAddress1(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Address 2"
                    value={jobAddress2}
                    onChange={e => setJobAddress2(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="City"
                    required={deliveryType === 'Pickup'}
                    value={jobCity}
                    onChange={e => setJobCity(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="State"
                    required={deliveryType === 'Pickup'}
                    value={jobState}
                    onChange={e => setJobState(e.target.value.toUpperCase().slice(0, 2))}
                    size="small"
                    inputProps={{ maxLength: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Zip"
                    required={deliveryType === 'Pickup'}
                    value={jobZip}
                    onChange={e => setJobZip(e.target.value)}
                    size="small"
                  />
                </Grid>
              </>
            )}

            {locationMode === 'coordinates' && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Latitude"
                    required={deliveryType === 'Pickup'}
                    value={jobLat}
                    onChange={e => setJobLat(e.target.value)}
                    size="small"
                    type="number"
                    inputProps={{ step: 'any', min: -90, max: 90 }}
                    helperText="Range: -90 to 90"
                    error={jobLat !== '' && (parseFloat(jobLat) < -90 || parseFloat(jobLat) > 90)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Longitude"
                    required={deliveryType === 'Pickup'}
                    value={jobLon}
                    onChange={e => setJobLon(e.target.value)}
                    size="small"
                    type="number"
                    inputProps={{ step: 'any', min: -180, max: 180 }}
                    helperText="Range: -180 to 180"
                    error={jobLon !== '' && (parseFloat(jobLon) < -180 || parseFloat(jobLon) > 180)}
                  />
                </Grid>
              </>
            )}
          </>
        )}

        {/* NH Warning Banner */}
        {nhWarning && !overrideMode && (
          <Grid item xs={12}>
            <Alert severity="warning" icon={<WarningAmberIcon />}>
              This jobsite is within a New Hampshire special case study zone. ASCE 7-16 may require
              a site-specific wind analysis. Please verify the load rating before proceeding.
            </Alert>
          </Grid>
        )}

        {/* Load Ratings Section */}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" mb={1} mt={2}>
            <Box sx={{ width: 4, height: 24, bgcolor: 'primary.main', borderRadius: 1, mr: 1 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Load Ratings
            </Typography>
          </Box>
        </Grid>

        {/* Live Load - always dropdown */}
        <Grid item xs={12} md={3}>
          <NumberField
            name="liveLoad"
            control={control}
            label="Live Load"
            isReadOnly={true}
            required={true}
            min={0}
            step={0.1}
            unitLabel={LoadUnitLabels.WEIGHT}
          />
        </Grid>

        {/* Dead Load - always dropdown */}
        <Grid item xs={12} md={3}>
          <NumberField
            name="deadLoad"
            control={control}
            label="Dead Load"
            isReadOnly={true}
            required={true}
            min={0}
            step={0.1}
            unitLabel={LoadUnitLabels.WEIGHT}
          />
        </Grid>

        {/* Building State - only shown in override mode */}
        {overrideMode && (
          <Grid item xs={12} md={3}>
            <StateField
              name="buildingState"
              control={control}
              label="Building State"
              required={true}
            />
          </Grid>
        )}

        {/* Wind Load */}
        <Grid item xs={12} md={3}>
          {overrideMode ? (
            <Controller
              name="windLoad"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Wind Load *</InputLabel>
                  <Select
                    {...field}
                    label="Wind Load *"
                    onChange={e => {
                      field.onChange(Number(e.target.value));
                      setValue('confirmedLoadRating', false, { shouldValidate: true });
                      onFormChange();
                    }}
                  >
                    {WIND_LOAD_OVERRIDE_OPTIONS.map(v => (
                      <MenuItem key={v} value={v}>
                        {v} mph
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          ) : (
            <ReadOnlyField
              label="Wind Load (ASCE Load Rating)"
              value={isLoadingAsce ? 'Loading...' : windLoad > 0 ? `${windLoad} mph` : '--'}
            />
          )}
        </Grid>

        {/* Snow Load */}
        <Grid item xs={12} md={3}>
          {overrideMode ? (
            <Controller
              name="snowLoad"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Snow Load *</InputLabel>
                  <Select
                    {...field}
                    label="Snow Load *"
                    onChange={e => {
                      field.onChange(Number(e.target.value));
                      setValue('confirmedLoadRating', false, { shouldValidate: true });
                      onFormChange();
                    }}
                  >
                    {SNOW_LOAD_OVERRIDE_OPTIONS.map(v => (
                      <MenuItem key={v} value={v}>
                        {v} psf
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          ) : (
            <ReadOnlyField
              label="Snow Load (ASCE Load Rating)"
              value={
                isLoadingAsce
                  ? 'Loading...'
                  : validationStatus === 'validated'
                    ? `${snowLoad} psf`
                    : '--'
              }
            />
          )}
        </Grid>

        {/* Manual Override Certification */}
        {overrideMode && (
          <Grid item xs={12}>
            <Box
              sx={{
                border: '2px solid',
                borderColor: 'error.main',
                borderRadius: 1,
                p: 2,
                mt: 1,
              }}
            >
              <Typography variant="subtitle2" color="error" fontWeight={600} sx={{ mb: 1 }}>
                Manual Load Rating Certification
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                {INDEMNIFICATION_TEXT}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={certificationChecked}
                    onChange={e => {
                      setCertificationChecked(e.target.checked);
                      setValue('manualLoadRatingCertified', e.target.checked);
                      onFormChange();
                    }}
                    color="error"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={500}>
                    I certify the above and accept responsibility for manually entered load ratings
                  </Typography>
                }
              />
            </Box>
          </Grid>
        )}

        {/* Confirm Load Rating */}
        <Grid item xs={12}>
          <Controller
            name="confirmedLoadRating"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!field.value}
                    onChange={e => {
                      field.onChange(e.target.checked);
                      onFormChange();
                    }}
                  />
                }
                label="Confirm Load Rating"
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Google Address Suggestion Dialog */}
      <Dialog
        open={suggestionDialogOpen}
        onClose={() => setSuggestionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Google Address Recommendation</DialogTitle>
        <DialogContent>
          {suggestedAddress && !suggestedAddress.isValid && suggestedAddress.errorMessage && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {suggestedAddress.errorMessage}
            </Alert>
          )}
          <Typography variant="body1" sx={{ mb: 2 }}>
            {suggestedAddress?.isValid
              ? 'Google Maps found a more accurate address. Would you like to use the suggested address?'
              : 'The address you entered could not be fully verified. Google Maps found a possible match below.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Original Address
              </Typography>
              <Typography variant="body2">
                {jobAddress1}
                {jobAddress2 ? `, ${jobAddress2}` : ''}
              </Typography>
              <Typography variant="body2">
                {jobCity}, {jobState} {jobZip}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                p: 2,
                border: '2px solid',
                borderColor: suggestedAddress?.isValid ? 'success.main' : 'warning.main',
                borderRadius: 1,
                bgcolor: suggestedAddress?.isValid ? '#f0fff0' : '#fffbf0',
              }}
            >
              <Typography
                variant="caption"
                color={suggestedAddress?.isValid ? 'success.main' : 'warning.main'}
              >
                Suggested Address
              </Typography>
              <Typography variant="body2">
                {suggestedAddress?.suggestedAddress?.address1}
              </Typography>
              <Typography variant="body2">
                {suggestedAddress?.suggestedAddress?.city},{' '}
                {suggestedAddress?.suggestedAddress?.state}{' '}
                {suggestedAddress?.suggestedAddress?.zip}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          {suggestedAddress?.isValid ? (
            <Button onClick={handleKeepOriginalAddress}>Keep My Address</Button>
          ) : (
            <Button onClick={() => setSuggestionDialogOpen(false)}>Go Back & Edit</Button>
          )}
          <Button variant="contained" onClick={handleUseSuggestedAddress}>
            Use Suggested Address
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog */}
      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Address Validation Failed</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{errorMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StepThree;
